import { getFontDisplayName } from './getFontDisplayName';

/**
 * Maps a VAS type enum value to a human-readable display prefix for LIP keys.
 * @param {string | undefined | null} vasType - The VAS type enum (e.g. 'UV', 'HOT_FOIL', 'EMBROIDERY').
 * @returns {string | null} The display prefix, or null if not recognisable.
 */
function formatVasTypePrefix(vasType) {
  if (!vasType) return null;
  switch (vasType) {
    case 'UV':
      return 'UV';
    case 'HOT_FOIL':
      return 'Hot Foil';
    case 'EMBROIDERY':
      return 'Embroidery';
    default:
      return vasType
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
  }
}

/**
 * Builds a VAS LIP key, appending placement when set.
 * e.g. "Product Hot Foil Colour Front" or "Product Hot Foil Colour" when placement is empty.
 * @param {string} vasTypePrefix
 * @param {string} segment - e.g. 'Colour', 'Jig', 'Font'
 * @param {string | undefined} placement
 * @returns {string}
 */
function buildVasLipKey(vasTypePrefix, segment, placement) {
  const placementSuffix =
    typeof placement === 'string' && placement.trim() ? ` ${placement.trim()}` : '';
  return `Product ${vasTypePrefix} ${segment}${placementSuffix}`;
}

/**
 * Sets a grouped VAS line-item property on acc, de-duplicating by value.
 * The first distinct value is written unsuffixed; subsequent DISTINCT values are
 * appended as _2, _3, etc. (never _1). If the value already exists under the base
 * key or any _N sibling, nothing is written — so grouped slots that share the same
 * value collapse to a single LIP (ALBAU-743).
 * e.g. values [A, A, B] → key=A, key_2=B
 * @param {Record<string, string>} acc
 * @param {string} key
 * @param {string} value
 */
function setWithSuffix(acc, key, value) {
  // eslint-disable-next-line security/detect-object-injection
  if (!(key in acc)) {
    // eslint-disable-next-line security/detect-object-injection
    acc[key] = value;
    return;
  }
  // Already captured under the base key — grouped duplicate, nothing to add.
  // eslint-disable-next-line security/detect-object-injection
  if (acc[key] === value) return;
  let i = 2;
  while (`${key}_${i}` in acc) {
    // eslint-disable-next-line security/detect-object-injection
    if (acc[`${key}_${i}`] === value) return;
    i++;
  }
  acc[`${key}_${i}`] = value;
}

/**
 * @param {Record<string, string>} acc
 * @param {import('../gateway/personalisationData').Slot | undefined} slot
 */
function applySlotVasBaseAttributes(acc, slot) {
  if (!slot?.vasConfig) return acc;

  const { vasConfig } = slot;
  if (vasConfig.vasType) {
    setWithSuffix(acc, '_vas_type', vasConfig.vasType);
  }
  if (vasConfig.templateId) {
    setWithSuffix(acc, '_template_id', vasConfig.templateId);
  }
  // ALBAU-743: Jig ID is now written under a suffixed key, so that grouped slots with the same Jig ID collapse to a single LIP.
  // This actually was requested to be removed.
  // if (vasConfig.hasJig && typeof vasConfig.jigId === 'string' && vasConfig.jigId.trim()) {
  //   setWithSuffix(acc, '_jig_id', vasConfig.jigId.trim());
  // }
  const vasTypePrefix = formatVasTypePrefix(vasConfig.vasType);
  const placement = typeof vasConfig.placement === 'string' ? vasConfig.placement.trim() : '';
  const colour = typeof vasConfig.colour === 'string' ? vasConfig.colour.trim() : '';

  if (vasTypePrefix && colour) {
    setWithSuffix(acc, buildVasLipKey(vasTypePrefix, 'Colour', placement), colour);
  }

  if (vasConfig.hasJig && typeof vasConfig.jigId === 'string' && vasConfig.jigId.trim() && vasTypePrefix) {
    setWithSuffix(acc, buildVasLipKey(vasTypePrefix, 'Jig', placement), vasConfig.jigId.trim());
  }

  return acc;
}

/**
 * @param {unknown} selected
 * @param {import('../gateway/personalisationData').Slot | undefined} slot
 */
function slotContributesBaseAttributes(selected, slot) {
  if (!slot || selected === null || selected === undefined || selected === '') {
    return false;
  }

  if (slot.slotType === 'IMAGE_UPLOAD' || slot.slotType === 'UV_INSERT') {
    return false;
  }

  const selectedValue = Array.isArray(selected) ? selected[0] : selected;

  if (typeof selectedValue === 'string' && selectedValue.includes('gid:/' + '/shopify/ProductVariant/')) {
    return true;
  }

  if (selectedValue && typeof selectedValue === 'object' && typeof selectedValue.text === 'string') {
    return selectedValue.text.trim().length > 0;
  }

  if (
    selectedValue &&
    typeof selectedValue === 'object' &&
    typeof selectedValue.translatedText === 'string' &&
    selectedValue.translatedText.trim().length > 0
  ) {
    return true;
  }

  return Boolean(selectedValue);
}

/**
 * True when the customer has filled this slot (not an empty / staging grouped copy).
 * @param {unknown} selected
 * @param {import('../gateway/personalisationData').Slot | undefined} slot
 * @param {string | null | undefined} groupLineItemPropertyKey
 */
function slotHasCustomerSelection(selected, slot, groupLineItemPropertyKey) {
  if (!slot || selected === null || selected === undefined || selected === '') {
    return false;
  }

  if (slot.slotType === 'IMAGE_UPLOAD') {
    const selectedValue = Array.isArray(selected) ? selected[0] : selected;
    if (!(selectedValue instanceof File)) return false;
    // GROUPED image copies propagated to inactive groups are not the active choice.
    if (slot.slotMode === 'GROUPED' && !groupLineItemPropertyKey) return false;
    return true;
  }

  if (slot.slotType === 'UV_INSERT') return false;

  return slotContributesBaseAttributes(selected, slot);
}

export const processBaseAttributes =
  ({ variants, personalisationSlots }) =>
  (acc, { lineItemPropertyKey, selected, id, groupLineItemPropertyKey }) => {
    const theSlot = personalisationSlots.get(id);

    const groupName = typeof theSlot?.groupName === 'string' ? theSlot.groupName.trim() : '';
    if (groupName && slotHasCustomerSelection(selected, theSlot, groupLineItemPropertyKey)) {
      acc['_Group Name'] = groupName;
    }

    // Skip IMAGE_UPLOAD slots — handled via _personalisation_choices.
    if (theSlot?.slotType === 'IMAGE_UPLOAD') {
      if (slotHasCustomerSelection(selected, theSlot, groupLineItemPropertyKey)) {
        applySlotVasBaseAttributes(acc, theSlot);
      }
      return acc;
    }

    // UV_INSERT slots are handled via _personalisation_choices.
    if (theSlot?.slotType === 'UV_INSERT') {
      return acc;
    }

    const isTranslatedSlot = theSlot?.translatedLineItemPropertyKey;

    if (isTranslatedSlot) {
      isTranslatedSlot.split(',').forEach((key) => {
        acc[key.trim()] = selected.translatedText;
      });
    }

    let lineItemPropertyKeyValue = null;
    // If the lineItemPropertyKey is not set, return null.
    if (!lineItemPropertyKey && !isTranslatedSlot) return acc;
    // If the selected value is a variant, set the lineItemPropertyKeyValue to the product title and variant title.
    const selectedValue = Array.isArray(selected) ? selected[0] : selected;
    // Only process strings as image binaries can pass through this function.
    if (typeof selectedValue === 'string' && selectedValue?.includes('gid:/' + '/shopify/ProductVariant/')) {
      const {
        product: { title: productTitle },
        title: variantTitle
      } = variants.get(selectedValue);
      lineItemPropertyKeyValue = `${productTitle}${variantTitle !== 'Default Title' ? ` - ${variantTitle}` : ''}`;
    } else if (selectedValue && typeof selectedValue.text === 'string') {
      const fontSelection = theSlot.allowedFonts?.includes(selectedValue.font);
      // If the selected value is a text, add the word 'Product' to the lineItemPropertyKey.
      const updatedLineItemPropertyKey = `Product ${lineItemPropertyKey}`;
      // Product engraving value - font or not.
      // eslint-disable-next-line security/detect-object-injection
      acc[updatedLineItemPropertyKey] = selectedValue.text;

      // Product engraving font value - if there are multiple fonts.
      // Checking against specific lintItemProperty key values to determine which custom engraving font LIP is required.
      // @TODO: Consider if there's a better way to handle this.
      const fontDisplayName = getFontDisplayName({ fontName: selectedValue.font });
      if (fontSelection && updatedLineItemPropertyKey.includes('Product Personalisation Back')) {
        acc['Custom Engraving Font Back'] = fontDisplayName;
      } else if (fontSelection && updatedLineItemPropertyKey.includes('Product Personalisation Inside')) {
        acc['Custom Engraving Font Inside'] = fontDisplayName;
      } else if (fontSelection && updatedLineItemPropertyKey.includes('Product Personalisation')) {
        acc['Custom Engraving Font Front'] = fontDisplayName;
      } else if (fontSelection) {
        const vasTypePrefix = theSlot?.vasConfig?.vasType ? formatVasTypePrefix(theSlot.vasConfig.vasType) : null;
        const placement = typeof theSlot?.vasConfig?.placement === 'string' ? theSlot.vasConfig.placement.trim() : '';

        if (vasTypePrefix) {
          // e.g. "Product Hot Foil Font Front" or "Product Hot Foil Font" when placement is empty
          const fontKey = buildVasLipKey(vasTypePrefix, 'Font', placement);
          // eslint-disable-next-line security/detect-object-injection
          acc[fontKey] = fontDisplayName;
        } else {
          // Legacy fallback when no vasConfig vasType is set.
          acc['Product Engraving Font'] = fontDisplayName;
        }
      }

      applySlotVasBaseAttributes(acc, theSlot);
      // Return the accumulator as we've added the items.
      return acc;
    } else if (selectedValue) {
      // Otherwise, set the lineItemPropertyKeyValue to the selected value.
      lineItemPropertyKeyValue = selectedValue;
    }

    if (lineItemPropertyKeyValue !== null && lineItemPropertyKey && typeof lineItemPropertyKey === 'string') {
      // eslint-disable-next-line security/detect-object-injection
      acc[lineItemPropertyKey] = lineItemPropertyKeyValue;
    }

    if (slotContributesBaseAttributes(selected, theSlot)) {
      applySlotVasBaseAttributes(acc, theSlot);
    }

    return acc;
  };
